import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';

interface Device {
  device_id: string;
  name: string;
  os: string;
  status: string;
  compliance_status: string;
  agent_id?: string;
  last_seen?: string;
}

const IOSManagement: React.FC = () => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);

  useEffect(() => {
    loadDevices();
  }, []);

  const loadDevices = async () => {
    try {
      const response = await apiService.tenantListDevices('ios');
      const iosDevices = (Array.isArray(response) ? response : []).map((device: any) => ({
        device_id: device.device_id || device.id,
        name: device.name || device.model || 'iOS Device',
        os: device.os || 'ios',
        status: device.status || 'active',
        compliance_status: device.compliance_status || 'unknown',
        last_seen: device.last_seen,
        platform_device_id: device.platform_device_id,
        profile_url: undefined as string | undefined,
      }));
      setDevices(iosDevices);
    } catch (err) {
      console.error('Error loading devices:', err);
    }
  };

  const handleProfileLink = async (deviceId: string) => {
    try {
      const info = await apiService.getIosProfileUrl(deviceId);
      if (info?.profile_url) {
        window.open(info.profile_url, '_blank');
      } else {
        window.alert('Profile URL not available yet. Complete enrollment first.');
      }
    } catch (err: any) {
      window.alert(err?.message || 'Failed to get MDM profile URL');
    }
  };

  const toggleDeviceSelection = (deviceId: string) => {
    setSelectedDevices(prev =>
      prev.includes(deviceId)
        ? prev.filter(id => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '8px', color: '#1f2937' }}>
          iOS Management
        </h1>
        <p style={{ color: '#6b7280', fontSize: '16px' }}>
          Manage iPhones and iPads
        </p>
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 className="card-title">Devices ({devices.length})</h2>
          <button className="action-btn">+ Enroll Device</button>
        </div>

        {devices.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
            <svg width="64" height="64" fill="#000000" viewBox="0 0 24 24" style={{ margin: '0 auto 16px', opacity: 0.5 }}>
              <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
            <p style={{ fontSize: '18px', fontWeight: '500', marginBottom: '8px' }}>No iOS devices enrolled</p>
            <p>Enroll iOS devices to start managing them</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="device-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={selectedDevices.length === devices.length && devices.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedDevices(devices.map(d => d.device_id));
                        } else {
                          setSelectedDevices([]);
                        }
                      }}
                    />
                  </th>
                  <th>Device Name</th>
                  <th>iOS Version</th>
                  <th>Status</th>
                  <th>Compliance</th>
                  <th>Last Seen</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((device) => (
                  <tr key={device.device_id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedDevices.includes(device.device_id)}
                        onChange={() => toggleDeviceSelection(device.device_id)}
                      />
                    </td>
                    <td style={{ fontWeight: '500', color: '#1f2937' }}>{device.name}</td>
                    <td style={{ color: '#4b5563' }}>{device.os}</td>
                    <td>
                      {(() => {
                        const isOnline = device.last_seen && (new Date().getTime() - new Date(device.last_seen).getTime() < 5 * 60 * 1000);
                        return (
                          <span className={`status-badge ${isOnline ? 'compliant' : 'warning'}`}>
                            {isOnline ? 'online' : 'offline'}
                          </span>
                        );
                      })()}
                    </td>
                    <td>
                      <span className={`status-badge ${device.compliance_status === 'compliant' ? 'compliant' : 'error'}`}>
                        {device.compliance_status}
                      </span>
                    </td>
                    <td style={{ color: '#6b7280' }}>
                      {device.last_seen ? new Date(device.last_seen).toLocaleString() : 'Never'}
                    </td>
                    <td>
                      <button
                        className="quick-action-btn"
                        style={{ fontSize: '12px', padding: '4px 8px', marginRight: 6 }}
                        onClick={() => handleProfileLink(device.device_id)}
                      >
                        MDM Profile
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default IOSManagement;
